import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { SvgIconComponent, injectRegisterIcons } from '@ngneat/svg-icon';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, fromEvent } from 'rxjs';

import { PermissionStoreModelEnum } from '@domain/profile';

import { IfPermissionDirective } from '@shared/directives/if-permission';
import { RouteSegments } from '@shared/enums';
import { BreakpointService } from '@shared/services/breakpoint';
import { ThemeService } from '@shared/services/theme';
import { CONFIG_TOKEN } from '@shared/tokens';

import { appAudit24Icon } from '@icons/px24/audit-24';
import { appCapacityManager24Icon } from '@icons/px24/capacity-manager-24';
import { appCrisis24Icon } from '@icons/px24/crisis-24';
import { appEleclinkOverview24Icon } from '@icons/px24/eleclink-overview-24';
import { appFile24Icon } from '@icons/px24/file-24';
import { appHelpDesk24Icon } from '@icons/px24/help-desk-24';
import { appHome24Icon } from '@icons/px24/home-24';
import { appImbalanceTrading24Icon } from '@icons/px24/imbalance-trading-24';
import { appNomination24Icon } from '@icons/px24/nomination-24';
import { appProfile24Icon } from '@icons/px24/profile-24';
import { appReport24Icon } from '@icons/px24/report-24';
import { appSettings24Icon } from '@icons/px24/settings-24';
import { appTransmissionRights24Icon } from '@icons/px24/transmission-rights-24';
import { appCancel32Icon } from '@icons/px32/cancel-32';
import { appHamburgerMenu32Icon } from '@icons/px32/hamburger-menu-32';

import { SideMenuItemComponent } from './side-menu-item';
import { SideMenuNestedItemComponent, SideMenuNestedItemHighlightViewModelEnum } from './side-menu-nested-item';
import { SideMenuService } from './side-menu.service';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SideMenuService],
  animations: [
    trigger('openClose', [
      state('mobile', style({ width: '100%' })),
      state('open', style({ width: '175px' })),
      state('closed', style({ width: '56px' })),
      transition('open <=> closed', [animate('150ms cubic-bezier(0.4, 0, 0.2, 1)')]),
    ]),
  ],
  imports: [
    RouterLink,
    SvgIconComponent,
    SideMenuItemComponent,
    TranslatePipe,
    IfPermissionDirective,
    SideMenuNestedItemComponent,
    RouterLinkActive,
  ],
})
export class SideMenuComponent implements AfterViewInit {
  readonly #config = inject(CONFIG_TOKEN);
  readonly #router = inject(Router);
  readonly #sideMenuService = inject(SideMenuService);
  readonly #breakpointService = inject(BreakpointService);
  readonly #themeService = inject(ThemeService);
  readonly #destroyRef = inject(DestroyRef);

  public navTemplate = viewChild<ElementRef<HTMLElement>>('nav');

  public navigationClick = output<void>();

  public readonly hasCrisis = this.#sideMenuService.hasCrisis;
  public readonly isLoggedIn = this.#sideMenuService.isLoggedIn;
  public readonly isDarkMode = this.#themeService.isDarkMode;
  public readonly isMobile = this.#breakpointService.isMobile;
  public readonly isTablet = this.#breakpointService.isTablet;
  public readonly isOnlyTablet = this.#breakpointService.isOnlyTablet;

  public readonly isCapacityMenuOpen = signal(false);
  public readonly isNominationMenuOpen = signal(false);
  public readonly isAdminMenuOpen = signal(false);
  public readonly isFileManagementMenuOpen = signal(false);

  public readonly isOpen = signal(!this.isOnlyTablet());
  public readonly isFullyOpened = signal(!this.isOnlyTablet());

  public readonly helpFaqUrl = this.#config.zendesk?.helpFaqUrl;

  protected readonly RouteSegments = RouteSegments;
  protected readonly PermissionStoreModelEnum = PermissionStoreModelEnum;
  protected readonly SideMenuNestedItemHighlightViewModelEnum = SideMenuNestedItemHighlightViewModelEnum;

  constructor() {
    injectRegisterIcons([
      appHome24Icon,
      appEleclinkOverview24Icon,
      appCapacityManager24Icon,
      appTransmissionRights24Icon,
      appNomination24Icon,
      appReport24Icon,
      appImbalanceTrading24Icon,
      appProfile24Icon,
      appAudit24Icon,
      appSettings24Icon,
      appFile24Icon,
      appHelpDesk24Icon,
      appCrisis24Icon,
      appCancel32Icon,
      appHamburgerMenu32Icon,
    ]);
    this.setSideMenuState(this.#router.url);
    this.watchNavigationEvents();
    this.watchBreakpointChanges();
  }

  public ngAfterViewInit(): void {
    this.watchOutsideClick();
  }

  public onNavClick(): void {
    if (this.isOpen() || !this.isOnlyTablet()) return;
    this.isOpen.set(true);
  }

  public toggle(e: MouseEvent): void {
    e.stopPropagation();
    this.isOpen.update((isOpen) => !isOpen);
  }

  public onOpenAnimationDone(): void {
    this.isFullyOpened.set(this.isOpen());
  }

  public setSideMenuState(url: string): void {
    this.isNominationMenuOpen.set(url.startsWith(`/${RouteSegments.Nominations}`));
    this.isAdminMenuOpen.set(url.startsWith(`/${RouteSegments.Admin}`));
    this.isFileManagementMenuOpen.set(url.startsWith(`/${RouteSegments.DataFlow}`) || url.startsWith(`/${RouteSegments.NominationUpload}`));
    this.isCapacityMenuOpen.set(
      url.startsWith(`/${RouteSegments.Capacity}`) || url.startsWith(`/${RouteSegments.Public}/${RouteSegments.Capacity}`)
    );
  }

  public onNavigationClick(event: MouseEvent): void {
    this.navigationClick.emit();

    if (this.isOnlyTablet()) {
      this.isOpen.set(false);
      event.stopPropagation();
    }
  }

  private watchNavigationEvents(): void {
    this.#router.events
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.setSideMenuState(this.#router.url);
      });
  }

  private watchBreakpointChanges(): void {
    effect(() => {
      const isOnlyTablet = this.isOnlyTablet();
      this.isOpen.set(!isOnlyTablet);
      this.isFullyOpened.set(!isOnlyTablet);
    });
  }

  private watchOutsideClick(): void {
    fromEvent(document.body, 'click')
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((event) => {
          return (
            this.isOnlyTablet() && !!this.navTemplate()?.nativeElement && !this.navTemplate()?.nativeElement.contains(event.target as Node)
          );
        })
      )
      .subscribe(() => {
        this.isOpen.set(false);
        this.isFullyOpened.set(false);
      });
  }
}
